import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';

interface WebhookHealthData {
  last_event_at: string | null;
  last_event_type: string | null;
  events_last_hour: number;
  events_last_24h: number;
  top_event_types_last_24h: Array<{ type: string; count: number }>;
  webhook_callback_url: string;
  registration_status: 'registered' | 'failed' | 'not_attempted';
  controller_verified: boolean | null;
  last_controller_verified_at: string | null;
  last_controller_verify_error: string | null;
  last_cleanup_at: string | null;
  last_cleanup_error: string | null;
  last_stale_subscription_id: string | null;
}

type ActionResponse = {
  data: {
    registration_status?: 'registered';
    subscription_id?: string;
    callback_url?: string;
    events?: string[];
    cleanup_status?: 'not_needed' | 'cleaned' | 'cleanup_failed';
    stale_subscription_id?: string | null;
    cleanup_error?: string | null;
    controller_verified?: boolean;
    mismatch_reason?: string | null;
  };
};

const STATUS_TONE: Record<WebhookHealthData['registration_status'], string> = {
  registered: 'bg-secondary-container text-on-secondary-container',
  failed: 'bg-error-container text-on-error-container',
  not_attempted: 'bg-tertiary-fixed text-on-tertiary-fixed',
};

type ActionKind = 'register' | 'rotate' | 'verify';

function actionPrompt(kind: ActionKind): {
  title: string;
  description: string;
  confirmLabel: string;
} {
  if (kind === 'register') {
    return {
      title: 'Re-register webhook',
      description:
        'Provide a reason for this re-registration. The action is audited and will attempt stale subscription cleanup.',
      confirmLabel: 'Re-register',
    };
  }
  if (kind === 'rotate') {
    return {
      title: 'Rotate secret + re-register',
      description:
        'Provide a reason for rotating the webhook secret. This action is audited and may require stale subscription cleanup follow-up.',
      confirmLabel: 'Rotate + re-register',
    };
  }
  return {
    title: 'Verify on controller',
    description:
      'Provide a reason for this verification run. The result is audited and updates controller verification state.',
    confirmLabel: 'Verify now',
  };
}

export function AdminWebhooksPage() {
  const [health, setHealth] = useState<WebhookHealthData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<ActionKind | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [dialogKind, setDialogKind] = useState<ActionKind | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiClient.get<{ data: WebhookHealthData }>(
        '/api/v1/admin/webhooks/health',
        { skipTenantScope: true },
      );
      setHealth(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load webhook health');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function runAction(kind: ActionKind, submittedReason: string) {
    setBusy(kind);
    setMessage(null);
    setError(null);
    try {
      const url =
        kind === 'register'
          ? '/api/v1/admin/webhooks/register'
          : kind === 'rotate'
            ? '/api/v1/admin/webhooks/rotate-secret'
            : '/api/v1/admin/webhooks/verify';
      const res = await apiClient.post<ActionResponse>(
        url,
        { reason: submittedReason },
        { skipTenantScope: true },
      );
      if (kind === 'verify') {
        setMessage(
          res.data.controller_verified
            ? 'Controller verification succeeded.'
            : `Controller verification failed: ${res.data.mismatch_reason ?? 'mismatch'}`,
        );
      } else {
        const label = kind === 'register' ? 'Registered' : 'Rotated + registered';
        const subscription = res.data.subscription_id
          ? ` subscription ${res.data.subscription_id.slice(0, 8)}…`
          : '';
        const cleanupSuffix =
          res.data.cleanup_status === 'cleanup_failed'
            ? ' (old subscription cleanup failed; follow-up required)'
            : '';
        setMessage(`${label}${subscription}${cleanupSuffix}`);
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Webhook action failed');
    } finally {
      setBusy(null);
    }
  }

  function openDialog(kind: ActionKind) {
    if (busy) return;
    setDialogKind(kind);
    setReason('');
    setReasonError(null);
  }

  function closeDialog() {
    if (busy) return;
    setDialogKind(null);
    setReason('');
    setReasonError(null);
  }

  async function submitDialog() {
    if (!dialogKind || busy) return;
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      setReasonError('Reason must be at least 5 characters.');
      return;
    }
    setReasonError(null);
    await runAction(dialogKind, trimmed);
    setDialogKind(null);
    setReason('');
  }

  if (!health && !error) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <header className="flex items-center gap-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
          <Icon name="sync" className="text-2xl" />
        </div>
        <div>
          <h1 className="font-headline-md text-headline-md text-on-surface">
            Webhook operations
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Diagnose inbound webhook flow and run registration recovery actions.
          </p>
        </div>
      </header>

      {message ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md"
        >
          {message}
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="bg-error-container text-on-error-container rounded-lg px-4 py-2 font-body-md text-body-md"
        >
          {error}
        </div>
      ) : null}

      {health ? (
        <>
          <section className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InfoRow label="Registration status">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${STATUS_TONE[health.registration_status]}`}
                >
                  {health.registration_status}
                </span>
              </InfoRow>
              <InfoRow label="Controller verified">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${
                    health.controller_verified === true
                      ? 'bg-secondary-container text-on-secondary-container'
                      : health.controller_verified === false
                        ? 'bg-error-container text-on-error-container'
                        : 'bg-tertiary-fixed text-on-tertiary-fixed'
                  }`}
                >
                  {health.controller_verified === true
                    ? 'verified'
                    : health.controller_verified === false
                      ? 'failed'
                      : 'not checked'}
                </span>
              </InfoRow>
              <InfoRow label="Callback URL">{health.webhook_callback_url}</InfoRow>
              <InfoRow label="Last event">{health.last_event_type ?? '—'}</InfoRow>
              <InfoRow label="Last seen">
                {health.last_event_at ? new Date(health.last_event_at).toLocaleString() : '—'}
              </InfoRow>
              <InfoRow label="Events (1h)">{String(health.events_last_hour)}</InfoRow>
              <InfoRow label="Events (24h)">{String(health.events_last_24h)}</InfoRow>
              <InfoRow label="Last verify">
                {health.last_controller_verified_at
                  ? new Date(health.last_controller_verified_at).toLocaleString()
                  : '—'}
              </InfoRow>
              <InfoRow label="Last cleanup">
                {health.last_cleanup_at ? new Date(health.last_cleanup_at).toLocaleString() : '—'}
              </InfoRow>
            </div>
            {health.last_controller_verify_error ? (
              <div className="rounded-lg bg-error-container text-on-error-container px-3 py-2 font-body-md text-body-md">
                Verify error: {health.last_controller_verify_error}
              </div>
            ) : null}
            {health.last_cleanup_error ? (
              <div className="rounded-lg bg-error-container text-on-error-container px-3 py-2 font-body-md text-body-md">
                Old subscription cleanup failed
                {health.last_stale_subscription_id
                  ? ` (${health.last_stale_subscription_id})`
                  : ''}
                : {health.last_cleanup_error}
              </div>
            ) : null}
          </section>

          <section className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 space-y-4">
            <h2 className="font-headline-sm text-headline-sm text-on-surface">
              Recovery actions
            </h2>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Registration and secret rotation actions are audited and update persisted
              registration state.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="ghost"
                onClick={() => openDialog('verify')}
                loading={busy === 'verify'}
                disabled={busy !== null}
              >
                Verify on controller
              </Button>
              <Button
                onClick={() => openDialog('register')}
                loading={busy === 'register'}
                disabled={busy !== null}
              >
                Re-register webhook
              </Button>
              <Button
                variant="secondary"
                onClick={() => openDialog('rotate')}
                loading={busy === 'rotate'}
                disabled={busy !== null}
              >
                Rotate secret + re-register
              </Button>
              <Button
                variant="ghost"
                onClick={() => void load()}
                disabled={busy !== null}
              >
                Refresh
              </Button>
            </div>
          </section>

          <section className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 space-y-3">
            <h2 className="font-headline-sm text-headline-sm text-on-surface">
              Top events (24h)
            </h2>
            {health.top_event_types_last_24h.length === 0 ? (
              <p className="font-body-md text-body-md text-on-surface-variant">
                No events received in the last 24 hours.
              </p>
            ) : (
              <ul className="space-y-2">
                {health.top_event_types_last_24h.map((row) => (
                  <li
                    key={row.type}
                    className="flex items-center justify-between border border-outline-variant rounded-lg px-3 py-2"
                  >
                    <span className="font-body-md text-body-md text-on-surface">{row.type}</span>
                    <span className="font-label-md text-label-md text-on-surface-variant">
                      {row.count}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
      {dialogKind ? (
        <div
          role="presentation"
          className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDialog();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="webhook-action-title"
            className="w-full max-w-lg rounded-xl border border-outline-variant bg-surface-container-lowest shadow-lg p-6 space-y-4"
            data-testid="webhook-action-dialog"
          >
            <h2
              id="webhook-action-title"
              className="font-headline-sm text-headline-sm text-on-surface"
            >
              {actionPrompt(dialogKind).title}
            </h2>
            <p className="font-body-md text-body-md text-on-surface-variant">
              {actionPrompt(dialogKind).description}
            </p>
            <div className="space-y-2">
              <label
                htmlFor="webhook-action-reason"
                className="font-label-md text-label-md uppercase text-on-surface-variant block"
              >
                Reason (min 5 characters)
              </label>
              <textarea
                id="webhook-action-reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                minLength={5}
                maxLength={500}
                required
                className="w-full px-3 py-2 rounded border border-outline-variant bg-surface text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="webhook-action-reason"
              />
              {reasonError ? (
                <div
                  role="alert"
                  className="text-error font-body-md text-body-md"
                  data-testid="webhook-action-reason-error"
                >
                  {reasonError}
                </div>
              ) : null}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={closeDialog} disabled={busy !== null}>
                Cancel
              </Button>
              <Button
                onClick={() => void submitDialog()}
                disabled={busy !== null || reason.trim().length < 5}
                loading={busy === dialogKind}
                data-testid="webhook-action-confirm"
              >
                {actionPrompt(dialogKind).confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function InfoRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="font-label-md text-label-md text-on-surface-variant uppercase">
        {label}
      </div>
      <div className="mt-1 font-body-md text-body-md text-on-surface break-all">{children}</div>
    </div>
  );
}
